import React from 'react';
import urlService from '../../services/urlService';
import FetchService from '../../services/fetchService';
import { getMapboxgl } from '../../services/mapboxService';
import { centerMap, getSelectedRideId, shouldRestartPoly, updateData } from '../../services/mapService';
import MapContext from '../../context/mapContext';
import {
  createLocationMarker,
  createPositionMarker,
  createVehicleMarker,
  hideLocationMarkers,
  hideMarker,
} from './Markers';
import { getToken } from '../../services/tokenService';
import { defaultLocation } from '../../const/defaultLocation';
import { isStorageItem } from '../../utils/isStorageItem';
import 'mapbox-gl/dist/mapbox-gl.css';
import { simplifyAddress } from '../../utils/simplifyAddress';

const mapIns = getMapboxgl();
const mapboxgl = mapIns.mapboxgl;

class Map extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      lng: props.lng || defaultLocation.longitude,
      lat: props.lat || defaultLocation.latitude,
      zoom: props.zoom || 8,
      vehicles: [],
      selectedRide: null,
      vehiclePosition: null,
      currentActiveRideHistory: [],
      pickupMarker: null,
      dropoffMarker: null,
      step: props.step || null,
    };

    this.mapContainer = React.createRef();
    this.countRetrieveEta = 0;
    this.oldStatus = null;
    this.etaInterval = null;

    this.resetMapToMaxHeight = this.resetMapToMaxHeight.bind(this);
    this.mapInit = this.mapInit.bind(this);
    this.updateMapPosition = this.updateMapPosition.bind(this);
    this.reportMapPosition = this.reportMapPosition.bind(this);

    this.getSelectedRide = this.getSelectedRide.bind(this);
    this.collectSelectedRide = this.collectSelectedRide.bind(this);

    this.drawVehicleOnMap = this.drawVehicleOnMap.bind(this);
    this.getOrCreateVehicle = this.getOrCreateVehicle.bind(this);
    this.getVehicle = this.getVehicle.bind(this);

    this.drawVehicleMarker = this.drawVehicleMarker.bind(this);
    this.getOrCreateVehicleMarker = this.getOrCreateVehicleMarker.bind(this);
    this.getVehicleMarker = this.getVehicleMarker.bind(this);

    this.allowVehicle = this.allowVehicle.bind(this);

    this.drawRideHistoryPolyline = this.drawRideHistoryPolyline.bind(this);
    this.getLocationMarkers = this.getLocationMarkers.bind(this);

    this.clearSelectedRide = this.clearSelectedRide.bind(this);
    this.retrieveEta = this.retrieveEta.bind(this);
    this.showMarkersBeforeRideBooked = this.showMarkersBeforeRideBooked.bind(this);
  }

  resetMapToMaxHeight = () => {
    const heightOfWindow = window.innerHeight;
    const isLandscapeMode = window.innerWidth > heightOfWindow;
    const slideUpScreen = document.querySelector('.slide-up-screen');
    let heightOfContent =
      (isLandscapeMode &&
        document.querySelector('.slide-up-container.open')?.clientHeight + slideUpScreen?.clientHeight) ||
      slideUpScreen?.clientHeight ||
      document.querySelector('.ride-header')?.clientHeight + document.querySelector('.footer')?.clientHeight ||
      0;
    const heightOfDownloadContent = document.querySelector('.download-wrapper')?.clientHeight || 0;
    heightOfContent =
      (isLandscapeMode && slideUpScreen && heightOfContent < heightOfWindow
        ? slideUpScreen?.clientHeight
        : heightOfContent) + heightOfDownloadContent || 0;
    let mapHeight = heightOfContent < heightOfWindow ? heightOfWindow - heightOfContent : heightOfContent;
    if (this.props.isWebBooker && !this.props.isWebBookerPortrait) mapHeight = heightOfWindow;
    if (!this.mapContainer.current) return;
    this.mapContainer.current.style.setProperty('height', `${mapHeight}px`);
    mapIns.mapbox?.resize();
  };

  mapInit = params => {
    const map = new mapboxgl.Map({
      container: this.mapContainer.current.id,
      style: 'mapbox://styles/curb/cjzvbze4v0f251cnu478ff9pd', // do not delete - custom style url
      center: window.location.pathname === '/tracker' ? [-73.972218, 40.76721] : [this.state.lng, this.state.lat],
      zoom: this.state.zoom,
      maxZoom: 18,
      ...params,
    });

    // add navigation control (the +/- zoom buttons)
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    if (!this.props.isWebBooker) map.addControl(new mapboxgl.FullscreenControl());

    mapIns.mapbox = map;
    const ctrlFullscreenNode = document.querySelector('.mapboxgl-ctrl-fullscreen');
    if (ctrlFullscreenNode) {
      const exitFullscreenNode = ctrlFullscreenNode.closest('.mapboxgl-ctrl.mapboxgl-ctrl-group');
      // $(document).on('click', '.mapboxgl-ctrl-shrink', e => {
      //   console.log('exit fullscreen ', e.view.document.fullscreen, e.view.document.fullscreenElement, e.view.document.fullscreenEnabled);
      //   if (!!e.view.document.fullscreenElement) window.scrollTo(0, 0);
      // });
      exitFullscreenNode.addEventListener('click', e => {
        console.log('e ', e);
        console.log(
          'exit fullscreen ',
          e.view.document.fullscreen,
          e.view.document.fullscreenElement,
          e.view.document.fullscreenEnabled,
        );
        if (!!e.view.document.fullscreenElement) {
          setTimeout(function () {
            document.webkitExitFullscreen();
            setTimeout(function () {
              window.scrollTo(0, 60);
            }, 2000);
          }, 2000);
          // setTimeout(function(){
          //   console.log('show the address bar');
          //   document.webkitExitFullscreen();
          //   // document.exitFullscreen();
          //   // This shows the address bar:
          //   // window.focus();
          //   // window.scrollTo(0, 1);
          // }, 2);
          // window.scrollTo(0, 1);
        }
      });
    }
    return map;
  };

  reportMapPosition = rideLocations => {
    const that = this;
    if (!(mapIns.mapbox && mapIns.mapbox.getBounds())) {
      return;
    }

    const ride = rideLocations || this.getSelectedRide(),
      bounds = new mapboxgl.LngLatBounds(),
      locationList = [];

    if (!ride || !Object.entries(ride)?.length) return;

    const pickup = ride.pickup_latitude && ride.pickup_longitude;
    const dropoff = ride.dropoff_latitude && ride.dropoff_longitude;
    const { vehicles = [] } = this.state;
    const [key] = Object.keys(vehicles);

    if (pickup) {
      locationList.push(new mapboxgl.LngLat(parseFloat(ride.pickup_longitude), parseFloat(ride.pickup_latitude)));
    }

    if (ride.marker && ride.status !== 'completed' && ride.status !== 'canceled' && ride.status !== 'missed') {
      locationList.push(new mapboxgl.LngLat(ride.marker._lngLat.lng, ride.marker._lngLat.lat));
    }

    if (ride.dropoff_location || dropoff) {
      locationList.push(new mapboxgl.LngLat(parseFloat(ride.dropoff_longitude), parseFloat(ride.dropoff_latitude)));
    }

    if (
      !ride.isVehicleHidden &&
      vehicles[key]?.marker &&
      ride.state !== 'completed' &&
      ride.state !== 'canceled' &&
      ride.state !== 'missed'
    ) {
      locationList.push(new mapboxgl.LngLat(vehicles[key].marker._lngLat.lng, vehicles[key].marker._lngLat.lat));
    }

    for (let i = 0; i < locationList.length; i++) {
      bounds.extend(locationList[i]);
    }

    mapIns.mapbox.on('bounds_changed', function (event) {
      this.setZoom(mapIns.mapbox.getZoom() - 1);
      if (this.getZoom() > 13) {
        this.setZoom(13);
      }
      if (this.getZoom() < 8) {
        this.setZoom(8);
      }
    });

    const options = {
      ...(((pickup && !dropoff) || (!pickup && dropoff)) && {
        maxZoom: that.props.isWebBookerPortrait ? mapIns.mapbox.getZoom() || 20 : 20,
      }),
      padding: { top: 120, bottom: 120, left: 120, right: 120 },
    };
    mapIns.mapbox.fitBounds(bounds, options);
  };

  updateMapPosition = (lat, lng) => {
    if (!lat || !lng) {
      return;
    }

    if (!mapIns.mapbox) {
      this.mapInit({
        center: [lng, lat], // Map center (optional)
      });
      window.setTimeout(function () {
        mapIns.mapbox.resize();
      }, 500);
    } else {
      centerMap({ mapbox: mapIns.mapbox, lng: lng, lat: lat, isLoaded: true, zm: this.state.zoom });
    }
    this.reportMapPosition();
  };

  getSelectedRide = () => {
    const { selectedRide: ride } = this.state;
    if (!ride) return;
    const status = ride.state;
    ride.isManualDispatchRide = status === 'manual_dispatch';
    ride.isUpcomingRide = ride.chronology === 'upcoming';
    ride.isActive = status !== 'completed' && status !== 'canceled' && status !== 'missed' && status !== 'review';
    ride.isExcludingSearching =
      status !== 'provider_submitted' &&
      status !== 'dispatch_pending' &&
      status !== 'submitted' &&
      !ride.isUpcomingRide &&
      !ride.isManualDispatchRide;
    this.setState({ selectedRide: ride });

    return ride;
  };

  collectSelectedRide = () => {
    const that = this;
    console.log('that.props.rideId ', that.props.rideId);
    const rideId = getSelectedRideId(that.props.rideId);

    // ridesService.loadRides(rideId, () => {
    //   this.updateMapPosition();
    //   this.clearSelectedRide();
    // }).then(details => {
    //
    // }).catch(error => {
    //   console.error(error);
    // });
    if (!rideId) {
      let ride;
      ride = { rideStatusMessage: 'Ride not found.' };
      this.props.showRide(ride);
      return;
    }
    // eslint-disable-next-line no-undef
    FetchService.getWebApiResource(urlService.getRideDetails(rideId))
      .then(async details => {
        if (that.props.updateStartingRideTime) {
          that.props.updateStartingRideTime(new Date());
        }
        // store.subscribe(() => {
        //   const { ride: rideFetched } = store.getState();
        const { ride: rideFetched } = details;

        that.setState({ selectedRide: rideFetched });
        const ride = that.getSelectedRide();

        if (ride && ride.isActive && ride.vehicle_number) {
          that.getLocationMarkers(ride);
          let vehicle = that.getOrCreateVehicle({
            vehicleNumber: ride.vehicle_number,
            imsi: ride.imsi,
          });

          if (vehicle && !vehicle.marker) {
            await that.drawVehicleOnMap(ride, function (result) {
              if (ride && ride.vehicle_number && ride.isExcludingSearching) {
                vehicle = that.getOrCreateVehicle({
                  vehicleNumber: ride.vehicle_number,
                  driver: result.driver,
                });
                const vehicleMarker = that.getVehicleMarker(ride.vehicle_number);
                if (vehicleMarker && vehicleMarker._map) {
                  that.reportMapPosition();
                }
              }
            });
          }
        } else if (ride && ride?.pickup_latitude && ride?.pickup_longitude) {
          that.updateMapPosition(parseFloat(ride.pickup_latitude), parseFloat(ride.pickup_longitude));
        }

        // that.drawRideHistoryPolyline(ride.vehicle_number, ride.ride_events);

        that.updateVehiclePosition(that);
        const vehiclePosition = setInterval(that.updateVehiclePosition, 10000, that);
        that.setState({ vehiclePosition });

        if (getToken().isToken()) {
          that.getLocationMarkers(ride);
          that.updateRideInformation();
        } else {
          that.updateMapPosition();
          that.clearSelectedRide();
        }
      })
      .catch(error => {
        console.error(error);
        that.updateMapPosition();
        that.clearSelectedRide();
      });
  };

  drawRideHistoryPolyline = (vehicleNumber, events) => {
    // service ?
    let event;
    const map = this.mapContainer.current;

    // skip forward to any manual dispatch event, we don't want to show the events before that point
    let startPoint = events.length - 1;
    for (; startPoint > 0; startPoint--) {
      event = events[startPoint];
      if (shouldRestartPoly(event.name)) {
        break;
      }
    }
    // let currentState = null;
    for (let i = startPoint; i < events.length; i++) {
      event = events[i];
      if (event.latitude !== null && event.longitude !== null) {
        if (event.name === 'position') {
          const markerInfo = {
            lat: parseFloat(event.latitude),
            lng: parseFloat(event.longitude),
            status: event.ride_status_code,
            eventAtLocal: event.event_at_local,
            map: mapIns.mapbox,
            that: map,
            rideId: event.ride_id,
          };
          createPositionMarker(markerInfo);
        } else {
          // currentState = event.name;
        }
      }
    }
  };

  onDragEnd = (lat, lng) => {
    const that = this;

    FetchService.getMapboxResource(urlService.reverseGeocoding(lat, lng))
      .then(response => {
        const { text, simple } = simplifyAddress(
          { text: response.features[0].place_name, latitude: lat, longitude: lng, isReversing: true },
          response.features[0],
        );

        const address = { text, simple };
        that.props.updatePickup(address);
        that.props.estimateFareList();
      })
      .catch(error => {
        console.error('No User Location');
      });
  };

  getLocationMarkers = async ride => {
    const that = this;
    // nested component ?
    const map = this.mapContainer.current;
    const pickupLocationContainer = map?.getElementsByClassName('location-pickup');
    const dropoffLocationContainer = map?.getElementsByClassName('location-dropoff');

    const { pickup_latitude, pickup_longitude, dropoff_latitude, dropoff_longitude } = ride || {};

    if (pickup_latitude && pickup_longitude) {
      const { pickupMarker } = this.state;
      if (!pickupLocationContainer?.length && !pickupMarker) {
        const pickupConfig = {
          lat: parseFloat(pickup_latitude),
          lng: parseFloat(pickup_longitude),
          isPickupLocation: true,
          draggable: this.props.isWebBookerPortrait && this.props.step === 9,
        };
        const pickupMarker = await createLocationMarker(pickupConfig);
        this.setState({ pickupMarker });
        pickupMarker.on('dragend', e => {
          const lngLat = e.target._lngLat;
          that.onDragEnd(lngLat.lat, lngLat.lng);
        });
      } else {
        pickupMarker?.setLngLat([pickup_longitude, pickup_latitude]);
      }
    }

    if (dropoff_latitude && dropoff_longitude) {
      const { dropoffMarker } = this.state;
      if (!dropoffLocationContainer?.length) {
        const dropoffConfig = {
          lat: parseFloat(dropoff_latitude),
          lng: parseFloat(dropoff_longitude),
          isPickupLocation: false,
        };
        const dropoffMarker = await createLocationMarker(dropoffConfig);
        this.setState({ dropoffMarker });
      } else {
        dropoffMarker?.setLngLat([dropoff_longitude, dropoff_latitude]);
      }
    }
  };

  drawVehicleOnMap = (ride, callbackFunction) => {
    const that = this;

    if (this.mapContainer.current) {
      if (ride && ride.vehicle_number && ride.vehicle_number !== 'N/A') {
        const providerKey = ride.fleet.key;

        // eslint-disable-next-line no-undef
        return FetchService.getWebApiResource(
          urlService.loadVehicleInfo({ providerKey, vehicleNumber: ride.vehicle_number }),
        )
          .then(result => {
            const latitude = parseFloat(result.latitude);
            const longitude = parseFloat(result.longitude);
            const serviceTypes = {
              serviceType: result.service_type_code,
              isAccessible: result.wheelchair_accessible,
            };
            if (ride.isExcludingSearching) {
              that.getOrCreateVehicleMarker({
                vehicleNumber: ride.vehicle_number,
                lat: latitude,
                lng: longitude,
                status: ride.status,
                heading: result.heading,
                serviceTypes,
                driver: result.driver,
              });
            }
            if (callbackFunction) {
              callbackFunction(result);
            }
          })
          .catch(error => {
            console.error(error);
          });
      }
    }
  };

  getNotesEditor = () => {
    const editor = isStorageItem('editor');
    return editor && editor.key === 'notes';
  };

  updateRideInformation = () => {
    const that = this;
    const isGuestBooking = that.props.isGuestBooking || that.props.isWebBooker;
    let message = 'Ride not found.',
      location = null;

    const id = isStorageItem('rideId');
    const ride = that.getSelectedRide();
    if (!ride) return;
    if (that.props.isBookerApp && id && parseInt(id) !== ride.id) return;

    const status = ride.state;

    const isRideStarted = status === 'enroute';
    const isRideCanceled = status === 'canceled' || status === 'missed' || status === 'review';
    const mapContainer = that.mapContainer.current;
    if ((!mapContainer && !that.getNotesEditor()) || (that.getNotesEditor() && !isRideStarted && !isRideCanceled)) {
      return;
    }

    switch (status) {
      case 'reassigning':
      case 'provider_submitted':
      case 'riderway_submitted':
      case 'manual_dispatch':
      case 'submitted':
        if (that.etaInterval) {
          clearInterval(that.etaInterval);
          that.etaInterval = null;
        }
        ride.eta = null;
        that.props?.updateInitialRideEta && that.props.updateInitialRideEta(null);
        ride.vehicle_number = null;
        that.countRetrieveEta = 0;
        message = status === 'manual_dispatch' ? 'No Driver' : isGuestBooking ? 'Requesting Your Ride' : 'Assigning';
        location =
          ride.pickup_line1 + ', ' + ride.pickup_city + ', ' + ride.pickup_state + ' ' + ride.pickup_postal_code || '';
        break;
      case 'assigned':
      case 'dispatched':
      case 'dispatch_pending':
        message = 'Ride Assigned';
        location =
          ride.pickup_line1 + ', ' + ride.pickup_city + ', ' + ride.pickup_state + ' ' + ride.pickup_postal_code || '';
        if (!that.countRetrieveEta) {
          that.countRetrieveEta++;
          that.retrieveEta(that, ride.vehicle_number, ride.pickup_latitude, ride.pickup_longitude);
          that.etaInterval = setInterval(
            that.retrieveEta,
            60000,
            that,
            ride.vehicle_number,
            ride.pickup_latitude,
            ride.pickup_longitude,
          );
        }
        break;
      case 'on_site':
        if (that.etaInterval) {
          clearInterval(that.etaInterval);
          that.etaInterval = null;
        }
        ride.eta = null;
        that.countRetrieveEta = 0;
        message = isGuestBooking ? 'Ride Arriving' : 'Ride Arrived';
        location =
          ride.pickup_line1 + ', ' + ride.pickup_city + ', ' + ride.pickup_state + ' ' + ride.pickup_postal_code || '';
        break;
      case 'enroute':
        message = 'In Ride';
        if (ride.dropoff_line1) {
          location =
            ride.dropoff_line1 +
              ', ' +
              ride.dropoff_city +
              ', ' +
              ride.dropoff_state +
              ' ' +
              ride.dropoff_postal_code || '';
          /*if (!that.countRetrieveEta) {
            *that.countRetrieveEta++;
            *that.retrieveEta(that, ride.vehicle_number, ride.dropoff_latitude, ride.dropoff_longitude);
            *that.etaInterval = setInterval(that.retrieveEta, 30000, that, ride.vehicle_number, ride.dropoff_latitude, ride.dropoff_longitude);
          } */
        }
        break;
      case 'completed':
      case 'canceled':
      case 'missed':
      case 'review':
        message = 'This ride is no longer in progress.';
        location = null;
        ride.eta = null;
        clearInterval(that.etaInterval);
        that.etaInterval = null;
        that.props?.updateInitialRideEta && that.props.updateInitialRideEta(null);
        const { vehiclePosition } = this.state;
        if (vehiclePosition) {
          clearInterval(vehiclePosition);
          that.setState({ vehiclePosition: null });
        }
        if (that.oldStatus !== status) {
          that.getLocationMarkers(ride);
          that.reportMapPosition();
        }
        break;
      default:
        clearInterval(that.etaInterval);
        break;
    }
    const driver = {
      first_name: ride.driver_name,
      phone_number: ride.driver_phone_number,
    };

    const vehicle = ride.vehicle_number
      ? that.getOrCreateVehicle({ vehicleNumber: ride.vehicle_number, driver: driver })
      : null;

    if ((status === 'reassigning' || status === 'manual_dispatch') && vehicle && vehicle.marker) {
      hideMarker(vehicle.marker);
    }

    if (that.oldStatus !== status) {
      that.reportMapPosition();
      that.oldStatus = ride.state;
    }
    ride.rideStatusMessage = message;
    ride.rideStatus = status;
    ride.location = location;
    ride.pickupMarker = that.state.pickupMarker || null;
    ride.dropoffMarker = that.state.dropoffMarker || null;
    this.setState({ selectedRide: ride });

    this.props.showRide(ride);

    if (
      getToken().isToken() &&
      vehicle &&
      vehicle.vehicleNumber &&
      status !== 'provider_submitted' &&
      status !== 'dispatch_pending' &&
      !ride.isUpcomingRide &&
      !ride.isManualDispatchRide
    ) {
      this.props.showVehicle(vehicle);
      this.resetMapToMaxHeight();
    } else {
      this.props.hideVehicle();
    }
  };

  clearSelectedRide = () => {
    if (this.state.selectedRide) {
      hideLocationMarkers();
      this.setState({ selectedRide: null });
    }
    this.updateRideInformation();
  };

  getOrCreateVehicle = ({ vehicleNumber, imsi, marker, heading, serviceTypes, driver }) => {
    const { vehicles } = this.state;
    const { first_name, phone_number } = driver || {};
    const vehicle = vehicles[vehicleNumber];
    if (!vehicle) {
      vehicles[vehicleNumber] = {
        marker: marker,
        vehicleNumber: vehicleNumber,
        driverName: first_name,
        driverPhoneNumber: phone_number,
        imsi: imsi,
        sessions: {},
        heading: heading,
        serviceType: serviceTypes?.serviceType || 'taxi',
      };
    } else {
      vehicle.vehicleNumber = vehicleNumber;
      if (imsi) {
        vehicle.imsi = imsi;
      }
      if (marker) {
        vehicle.marker = marker;
      }
      if (serviceTypes?.serviceType) {
        vehicle.serviceType = serviceTypes.serviceType;
      }
      if (heading || heading === 0) {
        vehicle.heading = heading;
      }
      if (first_name) {
        vehicle.driverName = first_name;
      }
      if (phone_number) {
        vehicle.driverPhoneNumber = phone_number;
      }
    }

    return vehicles[vehicleNumber];
  };

  getVehicle = vehicleNumber => {
    // nested component ?
    const { vehicles } = this.state;
    return vehicles[vehicleNumber];
  };

  drawVehicleMarker = (vehicleNumber, lat, lng, imsi) => {
    // nested component ? - VehicleMarker
    if (!vehicleNumber) console.error('No vehicle number given');
    const vehicle = this.getVehicle(vehicleNumber);
    if (!vehicle) return;

    return createVehicleMarker({ vehicle, vehicleNumber, lat, lng, imsi });
  };

  getOrCreateVehicleMarker = ({ vehicleNumber, lat, lng, imsi, status, heading, serviceTypes, driver }) => {
    const vehicle = this.getOrCreateVehicle({ vehicleNumber, imsi, heading, serviceTypes, driver });
    let vehicleMarker = vehicle.marker;

    if (!vehicleMarker) {
      if (!lat || !lng) return;
      vehicleMarker = this.drawVehicleMarker(vehicleNumber, lat, lng, imsi);
      if (this.allowVehicle(vehicleNumber) && mapIns.mapbox && (vehicleMarker || !vehicleMarker._map))
        vehicleMarker.addTo(mapIns.mapbox);
      vehicle.marker = vehicleMarker;
    } else {
      if (lat && lng) {
        vehicleMarker.setLngLat([lng, lat]);
      }
    }

    if (this.allowVehicle(vehicleNumber)) {
      if (mapIns.mapbox && (vehicleMarker || !vehicleMarker._map)) {
        // vehicleMarker.addTo(mapIns.mapbox);
        // if (mapIns.mapbox._loaded) {
        // vehicleMarker._popup.addTo(mapIns.mapbox);
        // } else {
        // mapIns.mapbox.on('load', function () {
        //   vehicleMarker._popup.addTo(mapIns.mapbox);
        // });
        // }
      }
    } else {
      vehicleMarker.remove();
    }

    vehicle.lastUpdate = new Date().getTime() / 1000;
    return vehicle;
  };

  getVehicleMarker = (vehicleNumber, lat, lng) => {
    // service ?
    const vehicle = this.getVehicle(vehicleNumber);
    const vehicleMarker = vehicle.marker;

    if (vehicleMarker && lat && lng) vehicleMarker.setLngLat([lng, lat]);

    return vehicleMarker;
  };

  allowVehicle = vehicleNumber => {
    const ride = this.getSelectedRide();
    let allow = true;

    if (allow && ride && !ride.vehicle_number) allow = false;

    if (allow && ride && ride.vehicle_number !== vehicleNumber) allow = false;

    if (allow && ride && !ride.isActive) allow = false;

    if (allow && ride && (ride.isUpcomingRide || ride.isManualDispatchRide)) allow = false;

    return allow;
  };

  retrieveEta = (that, vehicleNumber, endLatitude, endLongitude) => {
    const vehicle = that.getOrCreateVehicle({ vehicleNumber });
    const ride = that.getSelectedRide();

    if (ride?.state === 'on_site') return;

    if (vehicle && vehicle.marker && vehicle.marker._lngLat) {
      let params = {
        startLatitude: vehicle.marker._lngLat.lat,
        startLongitude: vehicle.marker._lngLat.lng,
        endLatitude: endLatitude,
        endLongitude: endLongitude,
      };
      FetchService.getMapboxResource(urlService.getMapboxEta(params))
        .then(response => {
          const receivedEta = Math.ceil(response.routes[0]?.duration / 60);
          ride.eta = receivedEta;
          that.props.setRideEta && that.props.setRideEta(receivedEta);
          if (!that.props.initialRideEta)
            that.props?.updateInitialRideEta && that.props.updateInitialRideEta(receivedEta);
          that.setState({ selectedRide: ride });
          that.updateRideInformation();
          return !!receivedEta;
        })
        .catch(() => {
          console.log('eta map error');
          if (ride) ride.eta = null;
          that.updateRideInformation();
        });
    } else {
      that.countRetrieveEta = 0;
    }
  };

  updateVehiclePosition = that => {
    const rideId = getSelectedRideId();
    const isUpcoming =
      that.state.selectedRide && that.state.selectedRide.chronology === 'upcoming' && !that.state.selectedRide.state;
    if (!rideId || isUpcoming) return;

    // eslint-disable-next-line no-undef
    FetchService.getWebApiResource(urlService.getRide(rideId))
      .then(details => {
        const { selectedRide: fullDataRide = {}, dropoffMarker } = that.state;

        if (fullDataRide?.dropoff_latitude !== details?.dropoff_location.latitude) {
          const newDropoffLat = details.dropoff_location.latitude;
          const newDropoffLng = details.dropoff_location.longitude;
          dropoffMarker?.setLngLat([newDropoffLng, newDropoffLat]);
        }

        updateData(fullDataRide || {}, details);
        that.setState({ selectedRide: fullDataRide });

        const selectedRide = that.getSelectedRide();

        that.updateRideInformation();

        if (
          !rideId ||
          !selectedRide ||
          !selectedRide.vehicle_number ||
          selectedRide.vehicle_number === 'N/A' ||
          (!selectedRide.isActive && !selectedRide.isUpcomingRide && !selectedRide.isManualDispatchRide)
        ) {
          return;
        }

        that.drawVehicleOnMap(selectedRide, function (result) {
          const latitude = parseFloat(result.latitude);
          const longitude = parseFloat(result.longitude);
          const vehicleNumber = result.vehicle_id;
          const status = selectedRide.state;
          const isActive = selectedRide.isActive;

          selectedRide.vehicle_number = vehicleNumber;
          selectedRide.isActive = isActive;

          // if (latitude && longitude && vehicleNumber && isActive &&
          //   !selectedRide.isUpcomingRide && !selectedRide.isManualDispatchRide) {
          //
          //   const { currentActiveRideHistory: currentPath } = that.state;
          //   currentPath.push({
          //     status: status,
          //     position: {
          //       latitude: latitude,
          //       longitude: longitude
          //     }
          //   });
          //   that.setState({ currentActiveRideHistory: currentPath });
          //   that.updateVehicleTripPolyline();
          // }

          let vehicle = selectedRide.vehicle_number ? that.getVehicle(selectedRide.vehicle_number) : null;
          if (!vehicle.driverPhoneNumber) {
            vehicle = selectedRide.vehicle_number
              ? that.getOrCreateVehicle({
                  vehicleNumber: selectedRide.vehicle_number,
                  driver: result.driver,
                })
              : null;
            if (
              getToken().isToken() &&
              vehicle &&
              vehicle.vehicleNumber &&
              !vehicle?.driverPhoneNumber &&
              selectedRide.state !== 'provider_submitted' &&
              selectedRide.state !== 'dispatch_pending' &&
              !selectedRide.isUpcomingRide &&
              !selectedRide.isManualDispatchRide
            ) {
              that.props.showVehicle(vehicle);
            }
          }

          if (!selectedRide.isExcludingSearching) {
            const { vehicles } = that.state;
            if (vehicles[vehicleNumber]) {
              delete vehicles[vehicleNumber];
            }
          }
        });
      })
      .catch(error => {
        console.error(error);
      });
  };

  updateVehicleTripPolyline = () => {
    let event;
    const map = this.mapContainer.current;
    if (!mapIns.mapbox) return;
    const { currentActiveRideHistory: pathTrip } = this.state;

    let startPoint = pathTrip.length - 1;
    for (; startPoint > 0; startPoint--) {
      event = pathTrip[startPoint];
      if (shouldRestartPoly(event.name)) {
        break;
      }
    }

    for (let i = startPoint; i < pathTrip.length; i++) {
      event = pathTrip[i];
      if (event.position && event.position.latitude !== null && event.position.longitude !== null) {
        const markerInfo = {
          lat: parseFloat(event.position.latitude),
          lng: parseFloat(event.position.longitude),
          status: event.status,
          eventAtLocal: event.event_at_local,
          map: mapIns.mapbox,
          that: map,
          rideId: event.ride_id,
        };
        createPositionMarker(markerInfo);
      }
    }
  };

  showMarkersBeforeRideBooked = (upcomingPickup, upcomingDropoff) => {
    let { step, isWebBooker, isWebBookerPortrait, pickup, dropoff } = this.props;
    if (upcomingPickup || upcomingDropoff) {
      pickup = { ...upcomingPickup };
      dropoff = { ...upcomingDropoff };
    }
    if (!(pickup || dropoff)) return;

    const ride = {
      pickup_latitude: pickup?.simple?.latitude,
      pickup_longitude: pickup?.simple?.longitude,
      ...(isWebBooker &&
        step !== 9 && {
          dropoff_latitude: dropoff?.simple?.latitude,
          dropoff_longitude: dropoff?.simple?.longitude,
        }),
      ...((upcomingPickup || upcomingDropoff) && {
        isVehicleHidden: true,
      }),
    };
    this.getLocationMarkers(ride);
    if (isWebBooker && step === 9) {
      centerMap({
        mapbox: mapIns.mapbox,
        lng: ride.pickup_longitude,
        lat: ride.pickup_latitude,
        isLoaded: mapIns.mapbox._loaded,
        zm: this.state.step > 8 ? (isWebBookerPortrait ? 17 : 15) : 12,
      });
    }
    this.reportMapPosition(ride);
  };

  componentDidUpdate = (prevProps, prevState) => {
    const {
      resetMapHeight,
      rideId,
      pickup,
      dropoff,
      vehicle,
      step,
      isSignupOfferVisible,
      doClearRide,
      isBookerApp,
      upcomingRidePickup,
      upcomingRideDropoff,
    } = this.props;
    const { pickupMarker, dropoffMarker, vehicles } = this.state;
    if (!isSignupOfferVisible && isSignupOfferVisible !== prevProps.isSignupOfferVisible) this.resetMapToMaxHeight();
    if (resetMapHeight && resetMapHeight !== prevProps.resetMapHeight) this.resetMapToMaxHeight();
    if (rideId !== prevProps.rideId) {
      if (rideId) this.collectSelectedRide();
      else this.setState({ selectedRide: null });
    }
    if (
      (pickup && pickup !== prevProps.pickup) ||
      (dropoff && dropoff !== prevProps.dropoff) ||
      (step && step !== prevProps.step)
    )
      this.showMarkersBeforeRideBooked();
    if (
      (pickup && !upcomingRidePickup && upcomingRidePickup !== prevProps.upcomingRidePickup) ||
      (dropoff && !upcomingRideDropoff && upcomingRideDropoff !== prevProps.upcomingRideDropoff)
    ) {
      this.showMarkersBeforeRideBooked();
      const vehicleLocation = document.getElementsByClassName('vehicle-marker')[0];
      vehicleLocation?.classList.remove('hidden');
    }
    if (
      (upcomingRidePickup && upcomingRidePickup !== prevProps.upcomingRidePickup) ||
      (upcomingRideDropoff && upcomingRideDropoff !== prevProps.upcomingRideDropoff)
    ) {
      this.showMarkersBeforeRideBooked(upcomingRidePickup, upcomingRideDropoff);
      const vehicleLocation = document.getElementsByClassName('vehicle-marker')[0];
      vehicleLocation?.classList.add('hidden');
    }

    if (
      isBookerApp &&
      ((!pickup && !upcomingRidePickup && pickup !== prevProps.pickup) ||
        (!pickup && !upcomingRidePickup && upcomingRidePickup !== prevProps.upcomingRidePickup) ||
        (!pickup && !upcomingRidePickup && pickupMarker && pickupMarker !== prevState.pickupMarker) ||
        (!dropoff && !upcomingRideDropoff && dropoff !== prevProps.dropoff) ||
        (!dropoff && !upcomingRideDropoff && upcomingRideDropoff !== prevProps.upcomingRideDropoff) ||
        (!dropoff && !upcomingRideDropoff && dropoffMarker && dropoffMarker !== prevState.dropoffMarker))
    ) {
      const pickupLocation = document.getElementsByClassName('location-pickup')[0];
      const dropoffLocation = document.getElementsByClassName('location-dropoff')[0];
      if (pickupMarker) {
        hideMarker(pickupMarker);
        this.setState({ pickupMarker: null });
      }
      if (dropoffMarker) {
        hideMarker(dropoffMarker);
        this.setState({ dropoffMarker: null });
      }
      if (pickupLocation) pickupLocation.remove();
      if (dropoffLocation) dropoffLocation.remove();
      this.updateMapPosition();
    }
    if (
      isBookerApp &&
      ((!vehicles?.length && JSON.stringify(vehicles) !== JSON.stringify(prevState.vehicles)) ||
        (!vehicle && vehicle !== prevProps.vehicle))
    ) {
      const vehicleLocation = document.getElementsByClassName('vehicle-marker')[0];
      if (vehicleLocation) hideMarker(vehicleLocation);
      this.updateMapPosition();
    }
    if (isBookerApp && doClearRide && doClearRide !== prevProps.doClearRide) {
      this.setState({ selectedRide: null, vehicles: [] });
      if (!(pickup && dropoff)) hideLocationMarkers();
      this.updateRideInformation();
    }
    if (isBookerApp && !pickup && !dropoff && this.props.lat !== prevProps.lat) {
      this.updateMapPosition(this.props.lat, this.props.lng);
    }
  };

  componentDidMount = () => {
    const that = this;
    const props = that.props;
    this.resetMapToMaxHeight();
    const map = this.mapInit();
    this.showMarkersBeforeRideBooked();
    this.collectSelectedRide();

    ['load', 'resize'].forEach(e => {
      window.addEventListener(e, that.resetMapToMaxHeight);
    });

    map.on('load', () => {
      if (!getToken().isToken()) {
        that.clearSelectedRide();
      }
      that.timer = setInterval(() => {
        if (!getToken().isToken()) {
          that.clearSelectedRide();
        }
      }, 10000);
    });

    map.on('touchmove', e => {
      if (e?.target?._zooming) return;

      if (props.isWebBookerPortrait && props.step === 9) {
        const { pickupMarker } = that.state;
        pickupMarker.setLngLat(map.getCenter());
      }
    });

    map.on('touchend', () => {
      if (props.isWebBookerPortrait && props.step === 9) {
        const mapCenter = mapIns.mapbox.getCenter();
        that.onDragEnd(mapCenter.lat, mapCenter.lng);
      }
    });
  };

  componentWillUnmount = () => {
    clearTimeout(this.timer);
  };

  render() {
    const contextValue = {
      map: mapIns.mapbox,
      mapContainer: this.mapContainer,
    };
    return (
      <MapContext.Provider value={contextValue}>
        <div
          id="map"
          ref={this.mapContainer}
          className={`map-container ${this.props.isSignupOfferVisible ? 'hidden' : ''} ${
            this.props.isCoveredByLogo ? 'covered-by-logo' : ''
          } ${this.props.isMobileView ? 'mobile-view-map' : ''}`}
        />
      </MapContext.Provider>
    );
  }
}

const withMapContextValue = Component => props => {
  return <MapContext.Consumer>{value => <Component {...props} {...value} />}</MapContext.Consumer>;
};

export default withMapContextValue(Map);
